import { useState, useEffect } from "react";
import { Calendar } from "@components/ui/calendar";
import { Button } from "@components/ui/button";
import { fr } from "date-fns/locale";
import { format } from "date-fns";
import { CircleX } from "lucide-react";

interface MultiDateCalendarProps {
  value: Date[] | null;
  onChange: (dates: Date[] | null) => void;
  now: Date;
  label?: string;
  numberOfMonths?: number;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

export const MultiDateCalendar = ({
  value,
  onChange,
  now,
  label = "Dates sélectionnées",
  numberOfMonths = 2,
  disabled = false,
  minDate,
  maxDate,
}: MultiDateCalendarProps) => {
  const [selected, setSelected] = useState<Date[]>(value ?? []);

  useEffect(() => {
    setSelected(value ?? []);
  }, [value]);

  const handleSelect = (dates: Date[] | undefined) => {
    if (!dates) {
      onChange(null);
      return;
    }

    // Sort dates chronologically
    const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
    onChange(sortedDates.length > 0 ? sortedDates : null);
  };

  const handleRemove = (dateToRemove: Date) => {
    const filtered = selected.filter(date => date.getTime() !== dateToRemove.getTime());
    onChange(filtered.length > 0 ? filtered : null);
  };

  const handleClearAll = () => {
    onChange(null);
  };

  const selectedCount = selected.length;

  const isDateDisabled = (date: Date) => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  // Set default month to minDate if available, otherwise use now
  const defaultMonth = minDate || now;

  return (
    <div className="flex flex-col space-y-4" data-testid="multi-date-calendar">
      <div className="flex w-full items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Date{selectedCount > 1 ? "s" : ""} sélectionnée{selectedCount > 1 ? "s" : ""} :{" "}
          <span className="text-xl font-bold text-primary">{selectedCount}</span>
        </p>
        <Button
          type="button"
          onClick={handleClearAll}
          disabled={selectedCount === 0 || disabled}
          variant="outline"
          size="sm"
          data-testid="clear-all-dates-button"
        >
          Effacer tout
        </Button>
      </div>

      <div className="flex w-full justify-center">
        <Calendar
          mode="multiple"
          selected={selected}
          onSelect={handleSelect}
          numberOfMonths={numberOfMonths}
          today={now}
          weekStartsOn={1}
          locale={fr}
          showOutsideDays={false}
          disabled={disabled ? disabled : isDateDisabled}
          defaultMonth={defaultMonth}
        />
      </div>

      {selectedCount > 0 && (
        <div className="flex flex-col space-y-2">
          <p className="text-sm font-medium">{label}</p>
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {selected.map(date => (
              <div key={date.getTime()} className="flex items-center justify-between rounded-md border bg-muted/50 px-3 py-2">
                <span className="text-sm">{format(date, "PPP", { locale: fr })}</span>
                {!disabled && (
                  <button
                    type="button"
                    className="cursor-pointer text-red-500 opacity-100 hover:text-red-700"
                    onClick={() => handleRemove(date)}
                    aria-label={`Supprimer la date ${format(date, "PPP", { locale: fr })}`}
                  >
                    <CircleX className="size-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
